/**
 * hvsin — 内置事件总线服务
 *
 * EventBusService 是容器的内置基础设施服务，提供通用事件订阅/发布能力。
 *
 * 设计目的：
 * - 替代 IModContainer 上的 on/off 方法，将事件能力从容器接口中剥离
 * - 容器在 Mod 生命周期关键点自动 emit 事件（mod:loaded / mod:unloaded 等）
 * - 业务 service 通过 DI 注入 IEventBusService 实例，可自由订阅任意事件
 * - 支持自定义事件类型，不限于容器预设的生命周期事件
 *
 * 使用方式（通过依赖注入获取）：
 * ```typescript
 * class MyService {
 *   constructor({ eventBusService }: { eventBusService: IEventBusService }) {
 *     eventBusService.on('mod:loaded', (event) => {
 *       console.log(`Mod loaded: ${event.modName}`)
 *     })
 *   }
 * }
 * ```
 */
/**
 * 事件总线服务
 *
 * 通用的事件订阅/发布实现，不绑定任何具体业务语义。
 * 容器在构造时自动创建实例并注册为 IEventBusService 接口。
 */
export class EventBusService {
    constructor() {
        /** 事件监听器映射：eventName → listeners[] */
        this.listeners = new Map();
    }
    /**
     * 订阅事件
     *
     * @param event 事件名称
     * @param listener 监听函数
     * @returns 取消订阅的函数
     */
    on(event, listener) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(listener);
        return () => this.off(event, listener);
    }
    /**
     * 订阅事件（只触发一次，之后自动取消）
     *
     * @param event 事件名称
     * @param listener 监听函数
     * @returns 取消订阅的函数
     */
    once(event, listener) {
        const wrapper = (payload) => {
            this.off(event, wrapper);
            return listener(payload);
        };
        return this.on(event, wrapper);
    }
    /**
     * 取消订阅
     *
     * @param event 事件名称
     * @param listener 要移除的监听函数
     */
    off(event, listener) {
        const list = this.listeners.get(event);
        if (!list)
            return;
        const index = list.indexOf(listener);
        if (index > -1) {
            list.splice(index, 1);
            if (list.length === 0) {
                this.listeners.delete(event);
            }
        }
    }
    /**
     * 发布事件（同步调用所有监听器）
     *
     * 监听器按注册顺序依次调用。如果某个监听器是 async，会等待其完成后再调用下一个。
     *
     * @param event 事件名称
     * @param payload 事件载荷
     */
    async emit(event, payload) {
        const list = this.listeners.get(event);
        if (!list || list.length === 0)
            return;
        // 复制一份，避免遍历过程中监听器被取消导致跳过
        const snapshot = [...list];
        for (const listener of snapshot) {
            try {
                await listener(payload);
            }
            catch (error) {
                console.error(`[EventBus] Listener for "${event}" failed:`, error);
            }
        }
    }
    /**
     * 移除某个事件的所有监听器
     *
     * @param event 事件名称（不传则清空所有事件的监听器）
     */
    clear(event) {
        if (event) {
            this.listeners.delete(event);
        }
        else {
            this.listeners.clear();
        }
    }
    /**
     * 获取某个事件的监听器数量
     */
    listenerCount(event) {
        return this.listeners.get(event)?.length ?? 0;
    }
}
//# sourceMappingURL=event-bus.js.map